import React from 'react';
import { matchPath } from 'react-router';
import { PageLayout, Content, Main, TopNavigation, LeftSidebar } from '@atlaskit/page-layout';
import { AtlassianNavigation } from '@atlaskit/atlassian-navigation';
import { SideNavigation, NavigationContent } from '@atlaskit/side-navigation';
import { routePaths } from '@css-xp/ppc-constants';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { LEFT_SIDEBAR } from '@css-xp/base/constants/left-sidebar';
import { BillingProfileDetailsPageLoader } from './billing-profile-details-page-loader';
import { CloudSitePageLoader } from './cloud-site-page-loader';
import { CustomerAddressDetailPageLoader } from './customer-address-detail-page-loader';
import { EntitlementDetailsPageLoader } from './entitlement-details-page-loader';
import { InvoiceDetailsPageLoader } from './invoice-details-page-loader';
import { OrderDetailsPageLoader } from './order-details-page-loader';
import { PageHeaderLoader } from './common/page-header-loader';
import { SearchPageLoader } from './search-page-loader';
import { ListPageLoader } from './list-page-loader';
import { CreditDetailsPageLoader } from './credit-details-page-loader';
import { EmailDetailsPageLoader } from './email-details-page-loader';
import { RefundsPageLoader } from './refunds-page-loader';
import { AccountBalancePageLoader } from './account-balance-page-loader';
import { OrganisationDetailPageLoader } from './organisation-detail-page-loader';
import { CatalogDetailsPageLoader } from './catalog-details-page-loader';
import { CatalogPageLoader } from './catalog-page-loader';
import { HomePageLoader } from './home-page-loader';
import { QuoteDetailPageLoader } from './quote-detail-page-loader';
import { OrderPageLoader } from './order-page-loader';
import './app-loader.scss';
import { SelectBillingProfilePageLoader } from './select-billing-profile-page-loader';

const pageToLoaderMap = {
  [routePaths.billingProfileDetails]: {
    element: <BillingProfileDetailsPageLoader />,
  },
  [routePaths.cloudSite]: {
    element: <CloudSitePageLoader />,
  },
  [routePaths.customerAddressDetailsById]: {
    element: <CustomerAddressDetailPageLoader />,
  },
  [routePaths.customerAddressDetailsByCompany]: {
    element: <CustomerAddressDetailPageLoader />,
  },
  [routePaths.entitlement]: {
    element: <EntitlementDetailsPageLoader />,
  },
  [routePaths.invoiceDetails]: {
    element: <InvoiceDetailsPageLoader />,
  },
  [routePaths.refundDetails]: {
    element: <RefundsPageLoader />,
  },
  [routePaths.orderDetails]: {
    element: <OrderDetailsPageLoader />,
  },
  [routePaths.search]: {
    element: <SearchPageLoader />,
  },
  [routePaths.creditDetails]: {
    element: <CreditDetailsPageLoader />,
  },
  [routePaths.customerAddresses]: {
    element: (
      <ListPageLoader hideFilters shouldRightAlignLastCol hidePageHeaderActions columnsCount={4} columnsWidth="15%" />
    ),
  },
  [routePaths.invoices]: {
    element: <ListPageLoader />,
  },
  [routePaths.subscriptions]: {
    element: <ListPageLoader />,
  },
  [routePaths.quotes]: {
    element: <ListPageLoader filtersCount={4} />,
  },
  [routePaths.ppcQuoteDetails]: {
    element: <QuoteDetailPageLoader />,
  },
  [routePaths.emailDetails]: {
    element: <EmailDetailsPageLoader />,
  },
  [routePaths.accountBalance]: {
    element: <AccountBalancePageLoader />,
  },
  [routePaths.organisationDetails]: {
    element: <OrganisationDetailPageLoader />,
  },
  [routePaths.home]: {
    element: <HomePageLoader />,
  },
  [routePaths.catalog]: {
    element: <CatalogPageLoader />,
  },
  [routePaths.catalogDetails]: {
    element: <CatalogDetailsPageLoader />,
  },
  [routePaths.uofSelectBillingProfile]: {
    element: <SelectBillingProfilePageLoader />,
  },
  [routePaths.orderV2]: {
    element: <OrderPageLoader />,
  },
};

export const AppLoader = () => {
  const pageName = Object.values(routePaths).find((routePath) =>
    matchPath(window.location.pathname, { path: routePath, exact: true })
  );
  const pageLoader = pageName && pageToLoaderMap[pageName] ? pageToLoaderMap[pageName].element : null;

  const renderTopNavigation = () => {
    return (
      <TopNavigation>
        <AtlassianNavigation
          label="site"
          testId="partner-app-navigation-loader"
          moreLabel="More"
          primaryItems={[
            <SkeletonLoaderWrapper key="1" className="app-loader-primary-items">
              <SkeletonLoader
                width="680px"
                height="32px"
                marginBottom="0"
                marginLeft="4px"
                classNameInner="search-loader-inner"
              />
            </SkeletonLoaderWrapper>,
            <SkeletonLoaderWrapper key="1" className="app-loader-primary-items">
              <SkeletonLoader width="92px" height="32px" marginBottom="0" marginLeft="8px" />
            </SkeletonLoaderWrapper>,
          ]}
          renderAppSwitcher={() => (
            <SkeletonLoaderWrapper>
              <SkeletonLoader width="30px" height="32px" marginRight="0.5rem" marginBottom="0" />
            </SkeletonLoaderWrapper>
          )}
          renderProductHome={() => (
            <SkeletonLoaderWrapper>
              <SkeletonLoader width="220px" height="32px" marginBottom="0" />
            </SkeletonLoaderWrapper>
          )}
          renderHelp={() => (
            <SkeletonLoaderWrapper>
              <SkeletonLoader width="32px" height="32px" circle marginBottom="0" marginRight="6px" />
            </SkeletonLoaderWrapper>
          )}
          renderSettings={() => (
            <SkeletonLoaderWrapper>
              <SkeletonLoader width="32px" height="32px" circle marginBottom="0" marginRight="6px" />
            </SkeletonLoaderWrapper>
          )}
          renderProfile={() => (
            <SkeletonLoaderWrapper>
              <SkeletonLoader width="32px" height="32px" circle marginBottom="0" />
            </SkeletonLoaderWrapper>
          )}
        />
      </TopNavigation>
    );
  };

  const renderLeftSidebar = () => {
    return (
      <LeftSidebar width={LEFT_SIDEBAR.WIDTH} id="left-sidebar-container">
        <SideNavigation label="Left sidebar">
          <NavigationContent>
            <SkeletonLoaderWrapper>
              <SkeletonLoader width="100%" height="52px" marginBottom="16px" marginTop="16px" />
            </SkeletonLoaderWrapper>
            {Array.from({ length: 7 }).map((_, index) => (
              <SkeletonLoaderWrapper key={index}>
                <SkeletonLoader width="70%" marginBottom="16px" />
              </SkeletonLoaderWrapper>
            ))}
          </NavigationContent>
        </SideNavigation>
      </LeftSidebar>
    );
  };

  return (
    <PageLayout testId="app-loader">
      {renderTopNavigation()}
      <Content>
        {renderLeftSidebar()}
        <Main id="main-content-v2">{pageLoader ? pageLoader : <DefaultLoader />}</Main>
      </Content>
    </PageLayout>
  );
};

const DefaultLoader = () => {
  return (
    <div data-testid="default-app-loader">
      <PageHeaderLoader />
      <SkeletonLoaderWrapper>
        <SkeletonLoader width="100%" height="20px" marginBottom="16px" />
        <SkeletonLoader width="100%" height="200px" />
      </SkeletonLoaderWrapper>
    </div>
  );
};
